import {
  type GetInvoiceReportEmployeeBillingBreakdownResponseAPI,
  type GetInvoiceReportEmployeesResponseAPI,
  type GetInvoiceReportsResponseAPI,
  type InvoiceReportAPI,
  type InvoiceReportEmployeeAPI,
  InvoiceReportNetSuiteTypeAPI,
  InvoiceReportStatusAPI,
  InvoiceReportTypeAPI,
  InvoiceSyncStatusTarget,
  InvoiceSyncStatusType,
  SyncAbortedStatusReasonType,
} from '@omnipresentgroup/types';

export const DEFAULT_INVOICE_REPORT: InvoiceReportAPI = {
  total_amount: {
    amount: 6800.45,
    currency: 'GBP',
  },
  total_amount_excluding_fees: {
    amount: 6800.45,
    currency: 'GBP',
  },
  id: 'inv-0008',
  invoice_type: InvoiceReportTypeAPI.DEPOSIT,
  issue_date: '2023-04-15',
  due_date: '2023-05-15',
  reference: 'April 2023 Invoice',
  status: InvoiceReportStatusAPI.PAID_IN_FULL,
  type: InvoiceReportNetSuiteTypeAPI.INVOICE,
  approved: false,
  employee_count: 4,
  company_id: 'adc8745b-f6fa-48a7-82f0-cf82ecd8b7t4',
  invoicing_emails: ['mail1@mail.com', 'mail2@mail.com'],
  sync_statuses: [
    {
      status: InvoiceSyncStatusType.SUCCESS,
      sync_target: InvoiceSyncStatusTarget.XERO,
      sync_attempted_at: '2023-04-15T12:00:00Z',
      xero_financial_document: {
        id: 'doc-0001',
        details: {
          url: 'https://xero.com/document/doc-0001',
        },
      },
    },
  ],
};

const generateInvoiceReport = (override: Partial<InvoiceReportAPI> = {}) => ({
  ...DEFAULT_INVOICE_REPORT,
  ...override,
});

export const PAID_INVOICE_REPORT = generateInvoiceReport({
  total_amount: {
    amount: 6002.03,
    currency: 'GBP',
  },
  total_amount_excluding_fees: {
    amount: 6002.03,
    currency: 'GBP',
  },
  due_date: '2022-10-05',
  id: 'inv-0001',
  invoice_type: InvoiceReportTypeAPI.DEPOSIT,
  issue_date: '2022-09-05',
  reference: 'September 2022 Invoice',
  status: InvoiceReportStatusAPI.PAID_IN_FULL,
  type: InvoiceReportNetSuiteTypeAPI.PAYMENT,
  approved: true,
});

export const FULLY_APPLIED_INVOICE_REPORT = generateInvoiceReport({
  total_amount: {
    amount: -1500.25,
    currency: 'GBP',
  },
  total_amount_excluding_fees: {
    amount: -1500.25,
    currency: 'GBP',
  },
  due_date: '2022-11-05',
  id: 'cred-0001',
  invoice_type: InvoiceReportTypeAPI.DEPOSIT,
  issue_date: '2022-11-05',
  parent_transaction_id: 'inv-0001',
  reference: 'September 2022 Credit',
  status: InvoiceReportStatusAPI.FULLY_APPLIED,
  type: InvoiceReportNetSuiteTypeAPI.CREDIT,
  approved: null,
});

export const DEPOSITED_INVOICE_REPORT = generateInvoiceReport({
  id: 'inv-0002',
  status: InvoiceReportStatusAPI.DEPOSITED,
  type: InvoiceReportNetSuiteTypeAPI.CREDIT,
  approved: null,
  sync_statuses: [
    {
      status: InvoiceSyncStatusType.ABORTED,
      sync_target: InvoiceSyncStatusTarget.XERO,
      sync_attempted_at: '2023-04-15T12:00:00Z',
      reason: {
        type: SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING,
        categories: [
          { id: 'cat-0001', details: { name: 'Category 1' } },
          { id: 'cat-0002', details: { name: 'Category 2' } },
        ],
        xero_organisation: {
          id: 'org-0001',
          details: {
            name: 'Organisation 1',
            url: 'https://xero.com/organisation/org-0001',
          },
        },
      },
    },
  ],
});

export const NOT_DEPOSITED_INVOICE_REPORT = generateInvoiceReport({
  id: 'inv-0003',
  status: InvoiceReportStatusAPI.NOT_DEPOSITED,
  sync_statuses: [
    {
      status: InvoiceSyncStatusType.ABORTED,
      sync_target: InvoiceSyncStatusTarget.XERO,
      sync_attempted_at: '2023-04-15T12:00:00Z',
      reason: {
        type: SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE,
        xero_contact: {
          id: 'con-0001',
          details: {
            name: 'Contact 1',
            url: 'https://xero.com/contact/con-0001',
          },
        },
        xero_organisation: {
          id: 'org-0001',
          details: {
            name: 'Organisation 1',
            url: 'https://xero.com/organisation/org-0001',
          },
        },
      },
    },
  ],
});

export const VOIDED_INVOICE_REPORT = generateInvoiceReport({
  id: 'inv-0004',
  status: InvoiceReportStatusAPI.VOIDED,
  sync_statuses: [
    {
      status: InvoiceSyncStatusType.ABORTED,
      sync_target: InvoiceSyncStatusTarget.XERO,
      sync_attempted_at: '2023-04-15T12:00:00Z',
      reason: {
        type: SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS,
        xero_organisation: {
          id: 'org-0001',
          details: {
            name: 'Organisation 1',
            url: 'https://xero.com/organisation/org-0001',
          },
        },
      },
    },
  ],
});

export const PAID_IN_FULL_INVOICE_REPORT = generateInvoiceReport({
  id: 'inv-0005',
  status: InvoiceReportStatusAPI.PAID_IN_FULL,
  sync_statuses: [
    {
      status: InvoiceSyncStatusType.ABORTED,
      sync_target: InvoiceSyncStatusTarget.XERO,
      sync_attempted_at: '2023-04-15T12:00:00Z',
      reason: {
        type: SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND,
      },
    },
  ],
});

export const OPEN_INVOICE_REPORT = generateInvoiceReport({
  id: 'inv-0006',
  status: InvoiceReportStatusAPI.OPEN,
  sync_statuses: [
    {
      status: InvoiceSyncStatusType.ABORTED,
      sync_target: InvoiceSyncStatusTarget.XERO,
      sync_attempted_at: '2023-04-15T12:00:00Z',
    },
  ],
});

export const DEFAULT_INVOICES_REPORTS = [
  DEFAULT_INVOICE_REPORT,
  PAID_INVOICE_REPORT,
  FULLY_APPLIED_INVOICE_REPORT,
  DEPOSITED_INVOICE_REPORT,
  NOT_DEPOSITED_INVOICE_REPORT,
  VOIDED_INVOICE_REPORT,
  PAID_IN_FULL_INVOICE_REPORT,
  OPEN_INVOICE_REPORT,
];

export const DEFAULT_INVOICE_REPORTS_RESPONSE: GetInvoiceReportsResponseAPI = {
  data: DEFAULT_INVOICES_REPORTS,
  count: DEFAULT_INVOICES_REPORTS.length,
};

export const DEFAULT_INVOICE: InvoiceReportAPI = {
  id: 'inv-0009',
  reference: 'April 2023 Invoice',
  due_date: '2023-05-15',
  issue_date: '2023-04-15',
  company_id: 'adc8745b-f6fa-48a7-82f0-cf82ecd8b7t4',
  total_amount: {
    amount: 6800.45,
    currency: 'GBP',
  },
  total_amount_excluding_fees: {
    amount: 6800.45,
    currency: 'GBP',
  },
  status: InvoiceReportStatusAPI.FULLY_APPLIED,
  type: InvoiceReportNetSuiteTypeAPI.INVOICE,
  parent_transaction_id: 'parentTransactionId',
  employee_count: 4,
  invoice_type: InvoiceReportTypeAPI.DEPOSIT,
  approved: false,
  invoicing_emails: ['email1@mail.com', 'email2@mail.com'],
};

const generateInvoice = (override: Partial<InvoiceReportAPI> = {}) => ({
  ...DEFAULT_INVOICE,
  ...override,
});

export const DEPOSITED_INVOICE = generateInvoice({
  id: 'inv-0001',
  status: InvoiceReportStatusAPI.DEPOSITED,
  approved: true,
});

export const NOT_DEPOSITED_INVOICE = generateInvoice({
  id: 'inv-0002',
  status: InvoiceReportStatusAPI.NOT_DEPOSITED,
  type: InvoiceReportNetSuiteTypeAPI.CREDIT,
  approved: null,
});

export const FULLY_APPLIED_INVOICE = generateInvoice({
  id: 'inv-0032',
  status: InvoiceReportStatusAPI.FULLY_APPLIED,
  approved: true,
});

export const DEFAULT_INVOICE_EMPLOYEE: InvoiceReportEmployeeAPI = {
  id: 'emp-0001',
  full_name: 'John Doe',
  email: 'johndoe@mail.com',
  salary: {
    amount: 2000,
    currency: 'GBP',
  },
  first_pay_date: '2023-04-15',
  last_pay_date: '2023-05-15',
  country: {
    name: 'United Kingdom',
    code: 'GB',
    flag: '🇬🇧',
  },
  role: 'Software Engineer',
  total_prev_invoice: {
    amount: 1000,
    currency: 'GBP',
  },
  billed_amount: {
    amount: 2000,
    currency: 'GBP',
  },
  billed_amount_excluding_fees: {
    amount: 2000,
    currency: 'GBP',
  },
  local_amount_excluding_fees: {
    amount: 2000,
    currency: 'GBP',
  },
  billing_notes: 'This is a billing note example',
  prev_invoices: [
    {
      pay_period: '2023-03-15',
      id: 'inv-0001',
    },
    {
      pay_period: '2023-02-15',
      id: 'inv-0002',
    },
  ],
};

const generateInvoiceEmployee = (
  override: Partial<InvoiceReportEmployeeAPI> = {},
) => ({
  ...DEFAULT_INVOICE_EMPLOYEE,
  ...override,
});

const SPANISH_INVOICE_EMPLOYEE: InvoiceReportEmployeeAPI =
  generateInvoiceEmployee({
    id: 'emp-0002',
    full_name: 'Juan Perez',
    country: {
      name: 'Spain',
      code: 'ES',
      flag: '🇪🇸',
    },
    role: 'Design',
    billing_notes: 'This is a Spanish note example',
    total_prev_invoice: {
      amount: 2000,
      currency: 'EUR',
    },
    billed_amount: {
      amount: 3000,
      currency: 'EUR',
    },
    billed_amount_excluding_fees: {
      amount: 3000,
      currency: 'GBP',
    },
    local_amount_excluding_fees: {
      amount: 3000,
      currency: 'EUR',
    },
    prev_invoices: [
      {
        pay_period: '2021-05-22',
        id: 'inv-0043',
      },
      {
        pay_period: '2000-02-21',
        id: 'inv-0065',
      },
    ],
  });

const DEFAULT_INVOICE_EMPLOYEES = [
  DEFAULT_INVOICE_EMPLOYEE,
  SPANISH_INVOICE_EMPLOYEE,
];

export const DEFAULT_INVOICE_REPORT_EMPLOYEES_RESPONSE: GetInvoiceReportEmployeesResponseAPI =
  {
    data: DEFAULT_INVOICE_EMPLOYEES,
    size: 1,
    pages: 1,
    total_count: DEFAULT_INVOICE_EMPLOYEES.length,
  };

export const DEFAULT_INVOICE_REPORT_EMPLOYEE_BREAKDOWN: GetInvoiceReportEmployeeBillingBreakdownResponseAPI =
  {
    id: 'emp-0001',
    pay_period: '2023-04-15',
    currency: 'GBP',
    data: {
      total_amount: 2050,
      items: [
        {
          label: 'Salary',
          amount: 4489,
          items: [],
        },
        {
          label: 'Allowances',
          amount: 151,
          items: [
            {
              label: 'Allowance 1',
              amount: 50,
              items: [],
            },
            {
              label: 'Allowance 2',
              amount: 61,
              items: [],
            },
          ],
        },
        {
          label: 'Benefits',
          amount: 76.3,
          items: [
            {
              label: 'Benefit 1',
              amount: 50.4,
              items: [
                {
                  label: 'Benefit 1',
                  amount: 50.4,
                  items: [
                    {
                      label: 'Benefit 1-1',
                      amount: 50.4,
                      items: [],
                    },
                  ],
                },
              ],
            },
            {
              label: 'Benefit 2',
              amount: 26,
              items: [],
            },
          ],
        },
        {
          label: 'Employer social costs',
          amount: 238.7,
          items: [],
        },
      ],
    },
  };

const generateInvoiceReportEmpoyeeBreakdown = (
  override: Partial<GetInvoiceReportEmployeeBillingBreakdownResponseAPI> = {},
) => ({
  ...DEFAULT_INVOICE_REPORT_EMPLOYEE_BREAKDOWN,
  ...override,
});

export const INVOICE_REPORT_EMPLOYEE_BREAKDOWNS = [
  DEFAULT_INVOICE_REPORT_EMPLOYEE_BREAKDOWN,
  generateInvoiceReportEmpoyeeBreakdown({
    id: 'emp-0002',
    pay_period: '2024-03-15',
    currency: 'EUR',
    data: {
      total_amount: 1000,
      items: [
        {
          label: 'Salary',
          amount: 4489,
          items: [],
        },
        {
          label: 'Allowances',
          amount: 2432,
          items: [
            {
              label: 'Allowance 1',
              amount: 4242,
              items: [],
            },
            {
              label: 'Allowance 2',
              amount: 23,
              items: [],
            },
          ],
        },
        {
          label: 'Benefits',
          amount: 76.3,
          items: [
            {
              label: 'Benefit 1',
              amount: 50.4,
              items: [
                {
                  label: 'Benefit 1-1',
                  amount: 50.4,
                  items: [],
                },
              ],
            },
            {
              label: 'Benefit 2',
              amount: 4343,
              items: [],
            },
          ],
        },
        {
          label: 'Employer social costs',
          amount: 2323.879,
          items: [],
        },
      ],
    },
  }),
];

export const INVOICE_REPORT_EMPLOYEE_BREAKDOWN_WITH_COMPARISON =
  generateInvoiceReportEmpoyeeBreakdown({
    compared_invoice: generateInvoiceReportEmpoyeeBreakdown({
      id: 'emp-0004',
      pay_period: '2022-03-15',
      currency: 'GBP',
      data: {
        total_amount: 1000,
        items: [
          {
            label: 'Salary',
            amount: 4489,
            items: [],
          },
          {
            label: 'Allowances',
            amount: 2432,
            items: [
              {
                label: 'Allowance 1',
                amount: 4242,
                items: [],
              },
              {
                label: 'Allowance 2',
                amount: 23,
                items: [],
              },
            ],
          },
          {
            label: 'Benefits',
            amount: 76.3,
            items: [
              {
                label: 'Benefit 1',
                amount: 50.4,
                items: [
                  {
                    label: 'Benefit 1-1',
                    amount: 50.4,
                    items: [],
                  },
                ],
              },
              {
                label: 'Benefit 2',
                amount: 4343,
                items: [],
              },
            ],
          },
          {
            label: 'Employer social costs',
            amount: 2323.879,
            items: [],
          },
        ],
      },
    }),
  });
